interface Division {
  title: string;
  subtitle: string;
  icon: string;
  color: string;
  tasks: string[];
}

const divisions: Division[] = [
  {
    title: "국제교육사업본부",
    subtitle: "International Education Division",
    icon: "ri-graduation-cap-line",
    color: "#1a2744",
    tasks: [
      "외국인 유학생 모집",
      "대학 입학 상담 및 지원",
      "비자 서류 지원",
      "한국 정착 지원",
      "한국어 교육 연계",
      "학교 협력 관리",
    ],
  },
  {
    title: "행사기획운영본부",
    subtitle: "Event Planning Division",
    icon: "ri-star-line",
    color: "#2d6a4f",
    tasks: [
      "축제·행사 기획 및 운영",
      "공연 기획 및 운영",
      "무대·음향·조명 운영",
      "행사 홍보 및 마케팅",
      "공공 행사 진행",
      "콘텐츠 행사 기획",
    ],
  },
  {
    title: "문화교류사업본부",
    subtitle: "Cultural Exchange Division",
    icon: "ri-team-line",
    color: "#1565c0",
    tasks: [
      "다문화 프로그램 운영",
      "국제 문화교류 사업",
      "체험형 문화 프로그램",
      "K-Culture 콘텐츠 운영",
      "체육·문화 프로그램 기획",
      "문화교류 네트워크 구축",
    ],
  },
  {
    title: "글로벌협력본부",
    subtitle: "Global Partnership Division",
    icon: "ri-earth-line",
    color: "#1b6b35",
    tasks: [
      "해외 에이전시 관리",
      "글로벌 파트너 발굴",
      "해외 학교 협력",
      "외국인 채용 협력",
      "MOU 체결 관리",
      "국제 네트워크 구축",
    ],
  },
  {
    title: "경영지원본부",
    subtitle: "Management Support Division",
    icon: "ri-clipboard-line",
    color: "#c0440a",
    tasks: [
      "인사 관리",
      "회계 관리",
      "계약 관리",
      "행정 지원",
      "문서 관리",
      "고객 지원",
    ],
  },
];

function ConnectorV() {
  return (
    <div
      className="w-px h-8 flex-shrink-0"
      style={{ backgroundColor: "#94a3b8" }}
    />
  );
}

function DivisionCard({ division }: { division: Division }) {
  return (
    <div className="w-full rounded-xl overflow-hidden border border-slate-200">
      <div className="p-3 text-center" style={{ backgroundColor: division.color }}>
        <div
          className="w-9 h-9 flex items-center justify-center mx-auto mb-2 rounded-lg"
          style={{ backgroundColor: "rgba(255,255,255,0.18)" }}
        >
          <i className={`${division.icon} text-white text-lg`} />
        </div>
        <p className="text-xs font-bold text-white leading-snug">{division.title}</p>
        <p className="text-[10px] mt-0.5 leading-tight" style={{ color: "rgba(255,255,255,0.65)" }}>
          {division.subtitle}
        </p>
      </div>
      <div className="bg-white p-3">
        <ul className="space-y-1.5">
          {division.tasks.map((task, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-navy-600">
              <span
                className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: division.color }}
              />
              <span>{task}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function OrganizationSection() {
  return (
    <section id="organization" className="relative w-full bg-white py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="animate-on-scroll text-center mb-12 md:mb-16">
            <p className="text-sky-500 text-sm font-semibold tracking-wider uppercase mb-3">
              Organization
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-navy-900">조직도</h2>
            <p className="text-navy-500 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              교육 · 문화 · 행사 · 글로벌 협력을 통합 운영하는 전문 기관
            </p>
          </div>

          {/* Org Chart */}
          <div className="flex flex-col items-center w-full">

            {/* Level 1 - 이사장 */}
            <div className="animate-on-scroll w-full max-w-xs sm:max-w-sm">
              <div
                className="rounded-xl px-6 py-5 text-center"
                style={{ backgroundColor: "#1a2744" }}
              >
                <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: "rgba(255,255,255,0.55)" }}>
                  Chairman
                </span>
                <p className="text-base md:text-lg font-bold text-white mt-1">이사장 장현이</p>
                <p className="text-xs mt-2 leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
                  기관 운영 자문 · 대외협력 · 전략기획 총괄
                </p>
              </div>
            </div>

            <ConnectorV />

            {/* Level 2 - 대표원장 */}
            <div className="animate-on-scroll w-full max-w-xs sm:max-w-sm">
              <div
                className="rounded-xl px-6 py-5 text-center"
                style={{ backgroundColor: "#2e6db4" }}
              >
                <span className="text-[11px] font-semibold tracking-widest uppercase" style={{ color: "rgba(255,255,255,0.55)" }}>
                  President &amp; Director
                </span>
                <p className="text-base md:text-lg font-bold text-white mt-1">대표원장 유진</p>
                <p className="text-xs mt-2 leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
                  법인 운영 총괄 · 사업 전략 · 대외업무 총괄
                </p>
              </div>
            </div>

            <ConnectorV />

            {/* Level 3 - 사내이사 */}
            <div className="animate-on-scroll w-full max-w-xs sm:max-w-sm">
              <div
                className="rounded-xl px-6 py-5 text-center border"
                style={{ backgroundColor: "#e8f0fe", borderColor: "#bfcfef" }}
              >
                <span className="text-[11px] font-semibold tracking-widest uppercase text-navy-500">
                  Executive Director
                </span>
                <p className="text-base md:text-lg font-bold text-navy-900 mt-1">사내이사 백종민</p>
              </div>
            </div>

            <ConnectorV />

            {/* Level 4 - 5 Divisions */}
            <div className="relative w-full animate-on-scroll">
              {/* Horizontal connector bar - desktop only */}
              <div
                className="hidden lg:block absolute top-0"
                style={{
                  left: "10%",
                  right: "10%",
                  height: "1px",
                  backgroundColor: "#94a3b8",
                }}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 w-full">
                {divisions.map((div, i) => (
                  <div key={i} className="flex flex-col items-center">
                    {/* Vertical drop - desktop only */}
                    <div
                      className="hidden lg:block w-px h-8 flex-shrink-0"
                      style={{ backgroundColor: "#94a3b8" }}
                    />
                    {/* Mobile/tablet top spacer */}
                    <div className="lg:hidden h-3" />
                    <DivisionCard division={div} />
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}